import { Box, Stack, Typography } from "@mui/material";

export interface DiagnosisDetailProps {
  summary: string;
  suggestedFix?: string | null;
  failedStep?: string | null;
  affectedSelector?: string | null;
  missingData?: string | null;
  notes?: string | null;
}

/**
 * Renders the body of a diagnosis card — summary, suggested fix callout,
 * and detail fields. Shared between DiagnosisTab and SelfHealingTab.
 */
export function DiagnosisDetail({
  summary,
  suggestedFix,
  failedStep,
  affectedSelector,
  missingData,
  notes,
}: DiagnosisDetailProps) {
  const hasDetails = failedStep ?? affectedSelector ?? missingData ?? notes;

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">{summary}</Typography>

      {suggestedFix && (
        <Box
          sx={{
            backgroundColor: "action.hover",
            borderLeft: 3,
            borderColor: "success.main",
            borderRadius: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: "block" }}>
            Suggested Fix
          </Typography>
          <Typography variant="body2">{suggestedFix}</Typography>
        </Box>
      )}

      {hasDetails && (
        <Stack spacing={1}>
          {failedStep && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" fontWeight={600}>
                Failed step:{" "}
              </Box>
              {failedStep}
            </Typography>
          )}

          {affectedSelector && (
            <Box
              component="code"
              sx={{
                display: "block",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                backgroundColor: "grey.100",
                borderRadius: 0.5,
                px: 1,
                py: 0.5,
                color: "text.secondary",
                overflowX: "auto",
              }}
            >
              Selector: {affectedSelector}
            </Box>
          )}

          {missingData && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" fontWeight={600}>
                Missing data:{" "}
              </Box>
              {missingData}
            </Typography>
          )}

          {notes && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" fontWeight={600}>
                Notes:{" "}
              </Box>
              {notes}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
